import { useState } from "react";

import { type PaymentProvider } from "@/api-v3";
import { brandingAssetUrl } from "@/features/branding";

import { getCardArtworkAssetType } from "../../utils/card-labels";

/** One retry, then the branded background colour stands in for the artwork. */
const MAX_BACKGROUND_ATTEMPTS = 2;

/**
 * The tenant's card artwork for the card's provider, absolutely positioned
 * behind the card fields. Retries once with a cache-busting url and then drops
 * out, leaving the parent's `bg-primary` so white card text stays legible.
 */
export const CardArtwork = ({ provider }: { provider: PaymentProvider }) => {
  const [attempt, setAttempt] = useState(1);
  const [failed, setFailed] = useState(false);

  const assetUrl = brandingAssetUrl(getCardArtworkAssetType(provider));
  const src = attempt === 1 ? assetUrl : `${assetUrl}?retry=${attempt}`;

  const handleError = () => {
    if (attempt < MAX_BACKGROUND_ATTEMPTS) {
      setAttempt((current) => current + 1);
      return;
    }
    setFailed(true);
    console.error(
      `Card background failed to load after ${MAX_BACKGROUND_ATTEMPTS} attempts`,
      src
    );
  };

  if (failed) {
    return null;
  }

  return (
    <img
      alt=""
      aria-hidden
      key={attempt}
      src={src}
      onError={handleError}
      className="absolute inset-0 h-full w-full object-cover object-center"
    />
  );
};
